// src/components/vision-board/VisionBoard.tsx
// Digital vision board with image upload and organization

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  ChevronLeft, Plus, Image as ImageIcon, Link, Trash2, X,
  Sparkles, Grid, LayoutGrid, Maximize2, Type
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { ToolHeader } from '../ui/ToolHeader';

interface VisionItem {
  id: string;
  user_id: string;
  image_url: string;
  caption: string | null;
  category: string | null;
  position: number;
  created_at: string;
}

const CATEGORIES = [
  { key: 'lifestyle', label: 'Lifestyle', color: 'bg-pink-100 text-pink-700' },
  { key: 'career', label: 'Career', color: 'bg-blue-100 text-blue-700' },
  { key: 'relationships', label: 'Relationships', color: 'bg-rose-100 text-rose-700' },
  { key: 'health', label: 'Health', color: 'bg-green-100 text-green-700' },
  { key: 'abundance', label: 'Abundance', color: 'bg-amber-100 text-amber-700' },
  { key: 'travel', label: 'Travel', color: 'bg-cyan-100 text-cyan-700' },
  { key: 'personal', label: 'Personal Growth', color: 'bg-purple-100 text-purple-700' },
];

interface VisionBoardProps {
  onClose?: () => void;
  embedded?: boolean;
}

export function VisionBoard({ onClose, embedded = false }: VisionBoardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [items, setItems] = useState<VisionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState<'url' | 'upload'>('url');
  const [viewMode, setViewMode] = useState<'grid' | 'masonry'>('grid');
  const [selectedItem, setSelectedItem] = useState<VisionItem | null>(null);
  
  // Add form state
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.id) loadItems();
  }, [user?.id]);

  const loadItems = async () => {
    if (!user?.id) return;
    setLoading(true);
    
    const { data, error } = await supabase
      .from('vision_board_items')
      .select('*')
      .eq('user_id', user.id)
      .order('position', { ascending: true });

    if (!error && data) {
      setItems(data);
    }
    setLoading(false);
  };

  const handleAddFromUrl = async () => {
    if (!user?.id || !imageUrl.trim()) return;
    setSaving(true);

    try {
      const { error } = await supabase.from('vision_board_items').insert({
        user_id: user.id,
        image_url: imageUrl.trim(),
        caption: caption.trim() || null,
        category: category || null,
        position: items.length,
      });

      if (!error) {
        resetForm();
        loadItems();
      }
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('vision-board')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('vision-board')
        .getPublicUrl(fileName);

      setImageUrl(urlData.publicUrl);
      setAddMode('url'); // Switch to show the URL input with the uploaded URL
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image. Try using a URL instead.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remove this from your vision board?')) return;

    await supabase.from('vision_board_items').delete().eq('id', id);
    loadItems();
    setSelectedItem(null);
  };

  const handleUpdateCaption = async (id: string, newCaption: string) => {
    await supabase
      .from('vision_board_items')
      .update({ caption: newCaption.trim() || null })
      .eq('id', id);
    loadItems();
  };

  const handleReorder = async (newOrder: VisionItem[]) => {
    setItems(newOrder);
    
    // Update positions in database
    const updates = newOrder.map((item, index) => ({
      id: item.id,
      position: index,
    }));

    for (const update of updates) {
      await supabase
        .from('vision_board_items')
        .update({ position: update.position })
        .eq('id', update.id);
    }
  };

  const resetForm = () => {
    setShowAddModal(false);
    setImageUrl('');
    setCaption('');
    setCategory('');
    setAddMode('url');
  };

  const getCategoryConfig = (cat: string | null) => {
    return CATEGORIES.find(c => c.key === cat) || null;
  };

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/chat');
    }
  };

  const wrapperClass = embedded
    ? "flex-1 overflow-y-auto"
    : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40";

  return (
    <div className={wrapperClass}>
      <ToolHeader
        title="Vision Board"
        onBack={handleClose}
        backLabel="Chat"
        rightAction={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'masonry' : 'grid')}
              className="p-2 text-purple-600 hover:bg-white/30 rounded-xl transition-all"
            >
              {viewMode === 'grid' ? <LayoutGrid className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 text-purple-600 hover:bg-white/30 rounded-xl transition-all"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        }
      />

      <div className="max-w-6xl mx-auto px-6">
        {loading ? (
          <div className="text-center py-20">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-purple-400 animate-pulse" />
            <p className="text-purple-500">Loading your vision...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-purple-500" />
            </div>
            <h3 className="text-lg font-semibold text-purple-800 mb-2">Your vision board is empty</h3>
            <p className="text-purple-600 mb-6 max-w-md mx-auto">
              Add images that represent your dreams, goals, and the life you're creating.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
            >
              Add Your First Image
            </button>
          </div>
        ) : (
          <Reorder.Group 
            axis="y" 
            values={items} 
            onReorder={handleReorder}
            className={`${
              viewMode === 'grid' 
                ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4' 
                : 'columns-2 md:columns-3 lg:columns-4 gap-4'
            }`}
          >
            {items.map((item) => (
              <Reorder.Item 
                key={item.id} 
                value={item}
                className={viewMode === 'masonry' ? 'mb-4 break-inside-avoid' : ''}
              >
                <motion.div
                  layoutId={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="relative group cursor-pointer rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-lg transition-shadow"
                >
                  <img
                    src={item.image_url}
                    alt={item.caption || 'Vision board item'}
                    className={`w-full object-cover ${viewMode === 'grid' ? 'aspect-square' : ''}`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400?text=Image+Not+Found';
                    }}
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      {item.caption && (
                        <p className="text-white text-sm font-medium truncate">{item.caption}</p>
                      )}
                      {item.category && (
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${getCategoryConfig(item.category)?.color || 'bg-gray-100 text-gray-700'}`}>
                          {getCategoryConfig(item.category)?.label || item.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expand icon */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="p-1.5 bg-white/90 rounded-full">
                      <Maximize2 className="w-4 h-4 text-purple-600" />
                    </div>
                  </div>
                </motion.div>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}

        {/* Add button floating */}
        {items.length > 0 && (
          <button
            onClick={() => setShowAddModal(true)}
            className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
            onClick={resetForm}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-purple-900">Add to Vision Board</h2>
                <button onClick={resetForm} className="p-2 text-gray-400 hover:text-gray-600 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setAddMode('url')}
                  className={`flex-1 py-2 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                    addMode === 'url' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Link className="w-4 h-4" />
                  Paste URL
                </button>
                <button
                  onClick={() => setAddMode('upload')}
                  className={`flex-1 py-2 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                    addMode === 'upload' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <ImageIcon className="w-4 h-4" />
                  Upload
                </button>
              </div>

              {addMode === 'url' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full p-3 rounded-xl border border-gray-200 focus:border-purple-400 outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste a link to any image from the web</p>
                </div>
              ) : (
                <div className="mb-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full py-8 border-2 border-dashed border-purple-200 rounded-xl flex flex-col items-center justify-center hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  >
                    {uploading ? (
                      <>
                        <Sparkles className="w-8 h-8 text-purple-400 animate-pulse mb-2" />
                        <span className="text-purple-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 text-purple-400 mb-2" />
                        <span className="text-purple-600">Click to upload</span>
                        <span className="text-xs text-gray-500 mt-1">Max 5MB</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Preview */}
              {imageUrl && (
                <div className="mb-4">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-xl"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Invalid+URL';
                    }}
                  />
                </div>
              )}

              {/* Caption */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Type className="w-4 h-4 inline mr-1" />
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="What does this represent?"
                  className="w-full p-3 rounded-xl border border-gray-200 focus:border-purple-400 outline-none"
                />
              </div>

              {/* Category */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Category (optional)</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => setCategory(category === cat.key ? '' : cat.key)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        category === cat.key ? cat.color + ' ring-2 ring-offset-1' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <button
                onClick={handleAddFromUrl}
                disabled={!imageUrl.trim() || saving}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? 'Adding...' : 'Add to Board'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full View Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              layoutId={selectedItem.id}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl max-h-[90vh] w-full"
            >
              <img
                src={selectedItem.image_url}
                alt={selectedItem.caption || 'Vision board item'}
                className="w-full h-full object-contain rounded-xl"
              />
              
              {/* Info bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-xl">
                <input
                  type="text"
                  value={selectedItem.caption || ''}
                  onChange={(e) => {
                    setSelectedItem({ ...selectedItem, caption: e.target.value });
                  }}
                  onBlur={() => {
                    if (selectedItem) handleUpdateCaption(selectedItem.id, selectedItem.caption || '');
                  }}
                  placeholder="Add a caption..."
                  className="w-full bg-transparent text-white text-lg placeholder-white/50 border-none outline-none"
                />
                {selectedItem.category && (
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm ${getCategoryConfig(selectedItem.category)?.color}`}>
                    {getCategoryConfig(selectedItem.category)?.label}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="absolute top-4 right-4 flex gap-2">
                <button
                  onClick={() => handleDelete(selectedItem.id)}
                  className="p-2 bg-white/10 hover:bg-red-500 text-white rounded-full transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default VisionBoard;
